import { useState, useEffect } from 'react';
import { Eye, Calendar, Mail, Monitor, Smartphone, Globe, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';
import { adminGetQuizResponses, adminGetQuizResponse } from '@/lib/api';
import { toast } from 'sonner';

const AdminResponses = () => {
  const [responses, setResponses] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [selectedResponse, setSelectedResponse] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [sourceFilter, setSourceFilter] = useState('all');
  const [page, setPage] = useState(0);
  const limit = 20;

  useEffect(() => {
    loadResponses();
  }, [sourceFilter, page]);

  const loadResponses = async () => {
    try {
      setLoading(true);
      const source = sourceFilter === 'all' ? null : sourceFilter;
      const data = await adminGetQuizResponses(limit, page * limit, source);
      setResponses(data.responses || []);
      setTotal(data.total || 0);
    } catch (error) {
      toast.error('Failed to load responses');
    } finally {
      setLoading(false);
    }
  };

  const viewDetails = async (response) => {
    try {
      const fullResponse = await adminGetQuizResponse(response.id);
      setSelectedResponse(fullResponse);
      setDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load response details');
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getSourceIcon = (source) => {
    switch (source) {
      case 'popup':
        return <Globe className="w-4 h-4" strokeWidth={1.5} />;
      case 'embed':
      case 'shopify':
        return <Monitor className="w-4 h-4" strokeWidth={1.5} />;
      case 'banner':
        return <Smartphone className="w-4 h-4" strokeWidth={1.5} />;
      default:
        return <Monitor className="w-4 h-4" strokeWidth={1.5} />;
    }
  };

  const getSourceBadge = (source) => {
    const colors = {
      web: 'bg-blue-100 text-blue-700',
      popup: 'bg-purple-100 text-purple-700',
      embed: 'bg-green-100 text-green-700',
      shopify: 'bg-green-100 text-green-700',
      banner: 'bg-orange-100 text-orange-700'
    };
    return (
      <Badge className={`${colors[source] || 'bg-stone-100 text-stone-700'} flex items-center gap-1`}>
        {getSourceIcon(source)}
        {source || 'web'}
      </Badge>
    );
  };

  const totalPages = Math.ceil(total / limit);

  if (loading && responses.length === 0) {
    return <div className="text-stone-400 p-8">Loading responses...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-serif text-2xl text-[#2C2C2C] mb-1">Quiz Responses</h1>
          <p className="text-stone-600 text-sm">{total} total responses</p>
        </div>
        <div className="flex items-center gap-4">
          <Select value={sourceFilter} onValueChange={setSourceFilter}>
            <SelectTrigger className="w-40" data-testid="source-filter">
              <SelectValue placeholder="All sources" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Sources</SelectItem>
              <SelectItem value="web">Web</SelectItem>
              <SelectItem value="embed">Embed</SelectItem>
              <SelectItem value="popup">Popup</SelectItem>
              <SelectItem value="banner">Banner</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {responses.length === 0 ? (
        <Card className="text-center py-12">
          <CardContent>
            <p className="text-stone-500">No quiz responses yet</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-4">
          {responses.map((response) => (
            <Card key={response.id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      {getSourceBadge(response.source)}
                      <span className="text-sm text-stone-500 flex items-center gap-1">
                        <Calendar className="w-4 h-4" strokeWidth={1.5} />
                        {formatDate(response.created_at)}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      {response.top_conditions?.map((cond, idx) => (
                        <Badge key={idx} variant="outline" className="text-xs">
                          {cond.consumer_label || cond.name}
                        </Badge>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-sm text-stone-600">
                      {response.recommended_protocol_name && (
                        <span>Protocol: <strong>{response.recommended_protocol_name}</strong></span>
                      )}
                      {response.email && (
                        <span className="flex items-center gap-1">
                          <Mail className="w-4 h-4" strokeWidth={1.5} />
                          {response.email}
                        </span>
                      )}
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => viewDetails(response)}
                    className="shrink-0"
                    data-testid={`view-response-${response.id}`}
                  >
                    <Eye className="w-4 h-4 mr-2" strokeWidth={1.5} />
                    View Details
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-center gap-4">
          <Button
            variant="outline"
            disabled={page === 0}
            onClick={() => setPage(page - 1)}
          >
            Previous
          </Button>
          <span className="text-sm text-stone-600">
            Page {page + 1} of {totalPages}
          </span>
          <Button
            variant="outline"
            disabled={page >= totalPages - 1}
            onClick={() => setPage(page + 1)}
          >
            Next
          </Button>
        </div>
      )}

      {/* Response Details Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-serif">Response Details</DialogTitle>
          </DialogHeader>
          {selectedResponse && (
            <div className="space-y-6 py-4">
              {/* Meta Info */}
              <div className="grid grid-cols-2 gap-4 p-4 bg-stone-50 rounded-lg">
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Date</p>
                  <p className="text-sm text-stone-700">{formatDate(selectedResponse.created_at)}</p>
                </div>
                <div>
                  <p className="text-xs text-stone-500 uppercase tracking-wide">Source</p>
                  <p className="text-sm text-stone-700">{selectedResponse.source || 'web'}</p>
                </div>
                {selectedResponse.email && (
                  <div className="col-span-2">
                    <p className="text-xs text-stone-500 uppercase tracking-wide">Email</p>
                    <p className="text-sm text-stone-700">{selectedResponse.email}</p>
                  </div>
                )}
              </div>

              {/* Matched Conditions */}
              <div>
                <h3 className="font-medium text-[#2C2C2C] mb-3">Matched Conditions</h3>
                <div className="space-y-2">
                  {selectedResponse.top_conditions?.map((cond, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-[#4A6763]/5 rounded-lg">
                      <span className="text-sm">{cond.consumer_label || cond.name}</span>
                      <Badge className="badge-primary">Score: {cond.score}</Badge>
                    </div>
                  ))}
                </div>
              </div>

              {/* Recommended Protocol */}
              {selectedResponse.recommended_protocol_name && (
                <div>
                  <h3 className="font-medium text-[#2C2C2C] mb-3">Recommended Protocol</h3>
                  <div className="p-4 border border-[#4A6763]/20 rounded-lg">
                    <p className="font-medium text-[#4A6763]">{selectedResponse.recommended_protocol_name}</p>
                    {selectedResponse.recommended_products?.length > 0 && (
                      <p className="text-sm text-stone-500 mt-1">
                        {selectedResponse.recommended_products.length} products recommended
                      </p>
                    )}
                  </div>
                </div>
              )}

              {/* All Answers */}
              <Collapsible>
                <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                  <h3 className="font-medium text-[#2C2C2C]">Full Answer Details</h3>
                  <ChevronDown className="w-5 h-5 text-stone-500" strokeWidth={1.5} />
                </CollapsibleTrigger>
                <CollapsibleContent className="mt-3">
                  <div className="space-y-3">
                    {Object.entries(selectedResponse.answer_labels || {}).map(([questionId, labels]) => (
                      <div key={questionId} className="p-3 bg-stone-50 rounded-lg">
                        <p className="text-xs text-stone-500 uppercase tracking-wide mb-1">
                          Question {questionId}
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {labels.map((label, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">
                              {label}
                            </Badge>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </CollapsibleContent>
              </Collapsible>

              {/* Condition Scores */}
              {selectedResponse.condition_scores && Object.keys(selectedResponse.condition_scores).length > 0 && (
                <Collapsible>
                  <CollapsibleTrigger className="flex items-center justify-between w-full p-3 bg-stone-50 rounded-lg hover:bg-stone-100 transition-colors">
                    <h3 className="font-medium text-[#2C2C2C]">All Condition Scores</h3>
                    <ChevronDown className="w-5 h-5 text-stone-500" strokeWidth={1.5} />
                  </CollapsibleTrigger>
                  <CollapsibleContent className="mt-3">
                    <div className="grid grid-cols-2 gap-2">
                      {Object.entries(selectedResponse.condition_scores)
                        .sort((a, b) => b[1] - a[1])
                        .map(([condId, score]) => (
                          <div key={condId} className="flex items-center justify-between p-2 bg-stone-50 rounded">
                            <span className="text-xs text-stone-600 truncate">{condId}</span>
                            <span className="text-xs font-medium text-[#4A6763]">{score}</span>
                          </div>
                        ))}
                    </div>
                  </CollapsibleContent>
                </Collapsible>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminResponses;
